/**
 * Shared lab-order status model.
 *
 * Junction returns compound dotted statuses and we key off the prefix;
 * Fullscript returns flat single words. Everything the UI needs is derived here
 * so no component parses a vendor status itself.
 *
 * `vendor` is optional on every helper input and defaults to "junction"; on
 * `@/types/lab` the field is required.
 *
 * Presentation agnostic — callers map category → styling.
 */
import type {
  LabCollectionMethod,
  LabInternalStatus,
  LabOrderStatus,
  LabVendor,
} from "@/types/lab";

// ---------------------------------------------------------------------------
// Lifecycle stages (list stage-tracker + detail timeline)
// ---------------------------------------------------------------------------

// Junction's four high-level order.status phases
// (received → collecting_sample → sample_with_lab → completed). Low-level
// sub-statuses like requisition_created stay within their phase — they change
// the badge label, not the stage.
export type LabStage = "ordered" | "collected" | "at_lab" | "results";

export const LAB_STAGES: { key: LabStage; label: string }[] = [
  { key: "ordered", label: "Ordered" },
  { key: "collected", label: "Collected" },
  { key: "at_lab", label: "At lab" },
  { key: "results", label: "Results" },
];

/**
 * Fullscript's lifecycle on the same four slots, so the dot layout is identical
 * across vendors. `not_purchased` sits before stage 0 and gets its own category.
 */
export const FULLSCRIPT_LAB_STAGES: { key: LabStage; label: string }[] = [
  { key: "ordered", label: "Purchased" },
  { key: "collected", label: "Scheduled" },
  { key: "at_lab", label: "Processing" },
  { key: "results", label: "Results" },
];

/**
 * Access's own two-slot arc. Access reports no order-status feed — "Collected"
 * and "At lab" are Junction/Fullscript milestones nothing will ever report for
 * an Access order, so they'd be permanent dead dots. Show only what results
 * ingestion can honestly derive: ordered, then results.
 */
export const ACCESS_LAB_STAGES: { key: LabStage; label: string }[] = [
  { key: "ordered", label: "Ordered" },
  { key: "results", label: "Results" },
];

/**
 * The in-house lab's arc. The clinic draws the tube itself and its analyzer
 * runs it, so nothing reports a collected or at-lab milestone: ordered, then
 * results.
 */
export const IN_HOUSE_LAB_STAGES: { key: LabStage; label: string }[] = [
  { key: "ordered", label: "Ordered" },
  { key: "results", label: "Results" },
];

// An uploaded order has no arc of its own and falls through to LAB_STAGES, but
// showsStageTracker() hides the tracker for it, so those stages are never shown.
export function getLabStages(
  vendor: LabVendor = "junction",
): { key: LabStage; label: string }[] {
  switch (vendor) {
    case "fullscript":
      return FULLSCRIPT_LAB_STAGES;
    case "access":
      return ACCESS_LAB_STAGES;
    case "in_house":
      return IN_HOUSE_LAB_STAGES;
    default:
      return LAB_STAGES;
  }
}

// ---------------------------------------------------------------------------
// Filter categories (a partition over every order)
// ---------------------------------------------------------------------------

export type LabStatusCategory =
  | "draft"
  | "awaiting_purchase" // recommended, but the patient hasn't checked out (Fullscript)
  | "in_progress"
  | "results_ready"
  | "completed"
  | "closed"; // cancelled / failed — terminal, no actionable results

export type LabBadgeVariant =
  | "default"
  | "neutral"
  | "success"
  | "warning"
  | "error";

export interface LabStatusMeta {
  /** Human-readable, high-level status (matches the redesign's labels). */
  label: string;
  category: LabStatusCategory;
  badgeVariant: LabBadgeVariant;
  /** Furthest lifecycle stage reached, as an index into getLabStages(vendor) (0–3). */
  stageIndex: number;
  /** Terminal = no further vendor progression (completed / cancelled / failed). */
  isTerminal: boolean;
}

/** Chips rendered in the orders list, in display order. `closed` is intentionally omitted. */
export const LAB_CHIP_CATEGORIES: {
  key: Exclude<LabStatusCategory, "closed">;
  label: string;
}[] = [
  { key: "in_progress", label: "In progress" },
  { key: "results_ready", label: "Results ready" },
  { key: "completed", label: "Completed" },
  { key: "draft", label: "Drafts" },
  // Fullscript only — a lab recommended to the patient that they have not bought
  // yet. Junction has no equivalent (its orders are placed by the clinic), so on
  // a Junction-only chart this chip renders a permanent zero. labs-tab filters it
  // out when the list has no such order, keeping today's four-chip row intact.
  { key: "awaiting_purchase", label: "Awaiting purchase" },
];

interface LabStatusInput {
  internalStatus: LabInternalStatus;
  vendorStatus?: LabOrderStatus | null;
  resultsViewedAt?: string | null;
  vendor?: LabVendor;
}

/** The high-level Junction status prefix. Junction-only — Fullscript states have no dots. */
export function vendorStatusPrefix(
  vendorStatus?: LabOrderStatus | null,
): string {
  return (vendorStatus ?? "received").split(".")[0];
}

function formatStatusSegment(segment: string): string {
  const sentence = segment.replace(/[_-]+/g, " ").toLowerCase();
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}

function vendorStatusEvent(
  vendorStatus?: LabOrderStatus | null,
): string | null {
  const segments = (vendorStatus ?? "").split(".");
  return segments.length >= 3 ? segments[segments.length - 1] : null;
}

function getReceivedStatusMeta(
  vendorStatus?: LabOrderStatus | null,
): Pick<LabStatusMeta, "label" | "badgeVariant" | "stageIndex"> {
  const eventSegment = vendorStatusEvent(vendorStatus);

  if (!eventSegment || eventSegment === "ordered") {
    return {
      label: "Ordered",
      badgeVariant: "default",
      stageIndex: 0,
    };
  }

  // A received sub-event (e.g. requisition_created) is still the "received"
  // phase per Junction — it only changes the badge label, not the stage dot.
  return {
    label: formatStatusSegment(eventSegment),
    badgeVariant: eventSegment === "partial_results" ? "warning" : "default",
    stageIndex: eventSegment === "partial_results" ? 3 : 0,
  };
}

/**
 * Derive everything the UI needs from an order's status fields.
 *
 * "Results ready" vs "Completed" is the unviewed/viewed split on an order whose
 * results have landed: results arrive → `results_ready` alert; a provider opens
 * it (stamping `resultsViewedAt`) → `completed`.
 */
export function getLabStatusMeta(order: LabStatusInput): LabStatusMeta {
  // Uploaded deliberately has no case of its own: its only vendor status is the
  // single value "completed", and Junction's completed-prefix case reads that
  // (plus a stamped resultsViewedAt) into exactly the right category —
  // "results_ready" until a provider views it, "completed" after. An order
  // created through the API arrives unviewed, so it shows as "Results ready".
  switch (order.vendor) {
    case "fullscript":
      return getFullscriptStatusMeta(order);
    case "access":
      return getAccessStatusMeta(order);
    case "in_house":
      return getInHouseStatusMeta(order);
    default:
      return getJunctionStatusMeta(order);
  }
}

/**
 * The in-house lab's statuses, which Decoda writes itself: "received" once the
 * lab interface has the order, then "partial" / "completed" as results come
 * back, or "cancelled". An order the interface didn't take is "failed". The
 * category matches what the Junction derivation gives these words (the server
 * reads them that way, see lab_status.py); only the labels are the lab's own.
 */
function getInHouseStatusMeta(order: LabStatusInput): LabStatusMeta {
  if (order.internalStatus === "draft") {
    return {
      label: "Draft",
      category: "draft",
      badgeVariant: "neutral",
      stageIndex: 0,
      isTerminal: false,
    };
  }
  // A failed order staff then cancelled reads as cancelled, under the switch below.
  if (order.internalStatus === "failed" && order.vendorStatus !== "cancelled") {
    return {
      label: "Not sent",
      category: "closed",
      badgeVariant: "error",
      stageIndex: 0,
      isTerminal: true,
    };
  }
  switch (order.vendorStatus) {
    case "partial":
      return {
        label: "Partial results",
        category: "in_progress",
        badgeVariant: "warning",
        stageIndex: 1,
        isTerminal: false,
      };
    case "completed": {
      const viewed = !!order.resultsViewedAt;
      return {
        label: viewed ? "Completed" : "Results ready",
        category: viewed ? "completed" : "results_ready",
        badgeVariant: "success",
        stageIndex: 1,
        isTerminal: true,
      };
    }
    case "cancelled":
      return {
        label: "Cancelled",
        category: "closed",
        badgeVariant: "error",
        stageIndex: 0,
        isTerminal: true,
      };
    // "received", or a value this build doesn't know yet.
    default:
      return {
        label: "Ordered",
        category: "in_progress",
        badgeVariant: "default",
        stageIndex: 0,
        isTerminal: false,
      };
  }
}

function getJunctionStatusMeta(order: LabStatusInput): LabStatusMeta {
  if (order.internalStatus === "draft") {
    return {
      label: "Draft",
      category: "draft",
      badgeVariant: "neutral",
      stageIndex: 0,
      isTerminal: false,
    };
  }
  if (order.internalStatus === "failed") {
    return {
      label: "Failed",
      category: "closed",
      badgeVariant: "error",
      stageIndex: 0,
      isTerminal: true,
    };
  }

  switch (vendorStatusPrefix(order.vendorStatus)) {
    case "completed": {
      const viewed = !!order.resultsViewedAt;
      return {
        label: viewed ? "Completed" : "Results ready",
        category: viewed ? "completed" : "results_ready",
        badgeVariant: "success",
        stageIndex: 3,
        isTerminal: true,
      };
    }
    case "sample_with_lab":
      return {
        label: "Sample with lab",
        category: "in_progress",
        badgeVariant: "warning",
        stageIndex: 2,
        isTerminal: false,
      };
    case "collecting_sample": {
      const eventSegment = vendorStatusEvent(order.vendorStatus);
      return {
        label: eventSegment
          ? formatStatusSegment(eventSegment)
          : "Collecting sample",
        category: "in_progress",
        badgeVariant: "warning",
        stageIndex: 1,
        isTerminal: false,
      };
    }
    case "cancelled":
      return {
        label: "Cancelled",
        category: "closed",
        badgeVariant: "error",
        stageIndex: 0,
        isTerminal: true,
      };
    case "failed":
      return {
        label: "Failed",
        category: "closed",
        badgeVariant: "error",
        stageIndex: 0,
        isTerminal: true,
      };
    case "received": {
      const meta = getReceivedStatusMeta(order.vendorStatus);
      return {
        ...meta,
        category: "in_progress",
        isTerminal: false,
      };
    }
    default:
      return {
        label: "Ordered",
        category: "in_progress",
        badgeVariant: "default",
        stageIndex: 0,
        isTerminal: false,
      };
  }
}

/**
 * Access's own status vocabulary — Access reports no order-status feed, so
 * this vocabulary is ours end to end: "ordered" at submit, then "partial" or
 * "complete" from results ingestion. Deliberately its own branch rather than
 * routed through Junction's dotted-string parser:
 * nothing Access sends will ever look like
 * "received.walk_in_test.requisition_created", so falling through to that
 * parser's `default:` case only happened to read right by coincidence.
 */
function getAccessStatusMeta(order: LabStatusInput): LabStatusMeta {
  if (order.internalStatus === "draft") {
    return {
      label: "Draft",
      category: "draft",
      badgeVariant: "neutral",
      stageIndex: 0,
      isTerminal: false,
    };
  }
  if (order.internalStatus === "failed") {
    return {
      label: "Failed",
      category: "closed",
      badgeVariant: "error",
      stageIndex: 0,
      isTerminal: true,
    };
  }

  switch (order.vendorStatus) {
    // Access results arrive per test as each completes, so a panel legitimately
    // sits here through several inbox rounds. Never an acknowledgeable "Results
    // ready" — mirrors Junction's received.*.partial_results.
    case "partial":
      return {
        label: "Partial results",
        category: "in_progress",
        badgeVariant: "warning",
        stageIndex: 1,
        isTerminal: false,
      };
    // Access re-sends the whole panel on completion, so this is the full set.
    case "complete": {
      const viewed = !!order.resultsViewedAt;
      return {
        label: viewed ? "Completed" : "Results ready",
        category: viewed ? "completed" : "results_ready",
        badgeVariant: "success",
        stageIndex: 1,
        isTerminal: true,
      };
    }
    // "ordered" (what submit writes), null, or a future value this build
    // doesn't recognize yet — never throw.
    default:
      return {
        label: "Ordered",
        category: "in_progress",
        badgeVariant: "default",
        stageIndex: 0,
        isTerminal: false,
      };
  }
}

/**
 * Fullscript's flat states on the same meta shape. `internalStatus` is always
 * "submitted" here, so the vendor state alone decides; unknown states fall
 * through to a neutral in-progress row rather than throwing.
 */
function getFullscriptStatusMeta(order: LabStatusInput): LabStatusMeta {
  switch (order.vendorStatus ?? "") {
    case "not_purchased":
      return {
        label: "Awaiting purchase",
        category: "awaiting_purchase",
        badgeVariant: "neutral",
        stageIndex: 0,
        isTerminal: false,
      };
    // `approved` is undocumented but real, observed live alongside `purchased`.
    case "purchased":
    case "approved":
      return {
        label: "Purchased",
        category: "in_progress",
        badgeVariant: "default",
        stageIndex: 0,
        isTerminal: false,
      };
    case "schedule_appointment":
      return {
        label: "Needs appointment",
        category: "in_progress",
        badgeVariant: "warning",
        stageIndex: 1,
        isTerminal: false,
      };
    case "upcoming_appointment":
      return {
        label: "Appointment scheduled",
        category: "in_progress",
        badgeVariant: "default",
        stageIndex: 1,
        isTerminal: false,
      };
    case "processing":
      return {
        label: "At lab",
        category: "in_progress",
        badgeVariant: "warning",
        stageIndex: 2,
        isTerminal: false,
      };
    case "partial_results":
      // Results have started landing but the order isn't finished, so it never
      // presents as an acknowledgeable "Results ready". Mirrors Junction.
      return {
        label: "Partial results",
        category: "in_progress",
        badgeVariant: "warning",
        stageIndex: 3,
        isTerminal: false,
      };
    // interpretation_ready groups here because the results themselves are in.
    case "results_ready":
    case "results_amended":
    case "interpretation_ready": {
      const viewed = !!order.resultsViewedAt;
      const amended = order.vendorStatus === "results_amended";
      return {
        // An amendment survives being read: acknowledging results means somebody
        // looked, not that the revision never happened. Two levels, because
        // "needs attention" and "is a revision" are different. The category still
        // flips to completed either way.
        label: amended
          ? viewed
            ? "Amended"
            : "Results amended"
          : viewed
            ? "Completed"
            : "Results ready",
        category: viewed ? "completed" : "results_ready",
        // An amendment is a correction, never a success: amber while unread, then
        // neutral — settled, without the green that reads as "finished clean".
        badgeVariant: amended ? (viewed ? "neutral" : "warning") : "success",
        stageIndex: 3,
        isTerminal: true,
      };
    }
    case "interpretation_shared":
      return {
        label: "Interpretation shared",
        category: "completed",
        badgeVariant: "success",
        stageIndex: 3,
        isTerminal: true,
      };
    default:
      return {
        label: "In progress",
        category: "in_progress",
        badgeVariant: "default",
        stageIndex: 0,
        isTerminal: false,
      };
  }
}

/** The stage tracker only adds signal for live orders + the results-ready moment. */
export function showsStageTracker(
  category: LabStatusCategory,
  vendor: LabVendor = "junction",
): boolean {
  // An uploaded order was never placed with a lab, so it has no ordered,
  // collected or at-lab milestones to show.
  if (vendor === "uploaded") return false;
  return category === "in_progress" || category === "results_ready";
}

/**
 * Whether results have landed, in whole or in part. The realtime cache hook uses
 * this to choose between patching a cached row and refetching.
 */
export function isResultsPhase(
  vendorStatus?: LabOrderStatus | null,
  vendor: LabVendor = "junction",
): boolean {
  if (vendor === "fullscript") {
    return (
      vendorStatus === "partial_results" ||
      vendorStatus === "results_ready" ||
      vendorStatus === "results_amended" ||
      vendorStatus === "interpretation_ready" ||
      vendorStatus === "interpretation_shared"
    );
  }
  if (vendor === "access") {
    // Both mean rows have landed: "partial" is a panel still filling in,
    // "complete" the whole set re-sent on completion.
    return vendorStatus === "partial" || vendorStatus === "complete";
  }
  if (vendor === "in_house") {
    return vendorStatus === "partial" || vendorStatus === "completed";
  }
  // Junction reports a half-arrived panel as `received.<method>.partial_results`,
  // which is not under the `completed` prefix, so the prefix alone would miss it.
  // Uploaded falls through here too: its only vendorStatus is "completed",
  // which startsWith("completed") reads correctly — no explicit arm needed.
  return (
    !!vendorStatus?.startsWith("completed") ||
    !!vendorStatus?.includes("partial_results")
  );
}

/**
 * Whether the lab's requisition form is available to view/download.
 *
 * Junction generates the requisition just after order creation and it stays
 * available for the rest of the order's life. On a fresh order the top-level
 * `vendorStatus` advances to `requisition_created` immediately (driving the
 * badge), while the matching granular event only lands later via webhook — so
 * gating on the event alone hides the action while the badge already reads
 * "Requisition created". Gate on the milestone itself: the `requisition_created`
 * (or any later) sub-status within "received", or any phase past "received".
 */
export function hasRequisitionAvailable(order: {
  internalStatus: LabInternalStatus;
  vendorStatus?: LabOrderStatus | null;
  vendor?: LabVendor;
}): boolean {
  if (order.vendor === "fullscript") {
    // A Fullscript lab is fulfilled from the patient's own Fullscript
    // checkout — there is no requisition form for the clinic to hand over.
    return false;
  }
  if (order.vendor === "access") {
    // Access DOES have a requisition — unlike Junction's, it's captured once,
    // inline, in Access's order-placement response at submit (see
    // submit_access_order / AccessLabAdapter.capture_requisition), so it's
    // available the instant the order exists, with no sub-status milestone to
    // wait for.
    return order.internalStatus === "submitted";
  }
  // An uploaded order was never placed with a lab, so there is no requisition to fetch.
  if (order.vendor === "uploaded") return false;
  // The in-house lab works from the tube's barcode; there is no requisition form.
  if (order.vendor === "in_house") return false;
  // Junction:
  if (order.internalStatus !== "submitted") return false;
  const prefix = vendorStatusPrefix(order.vendorStatus);
  if (
    prefix === "collecting_sample" ||
    prefix === "sample_with_lab" ||
    prefix === "completed"
  ) {
    return true;
  }
  // Within "received", the requisition exists once past the initial "ordered"
  // sub-status (bare "received" / "ordered" means it hasn't been generated yet).
  const sub = order.vendorStatus?.split(".").pop() ?? "";
  return (
    prefix === "received" &&
    sub !== "" &&
    sub !== "received" &&
    sub !== "ordered"
  );
}

/**
 * Whether printable specimen labels are offered for this order.
 *
 * On-site collection only. In every other modality whoever draws the sample
 * also labels it — an at-home test kit ships pre-registered, a mobile
 * phlebotomist brings their own supplies, and a walk-in PSC labels on the lab's
 * own system. On-site collection is the one modality where our clinic's staff
 * draw the sample, leaving them holding an unlabelled tube with no lab-side
 * label printer in the room.
 *
 * Delegates the milestone to `hasRequisitionAvailable` rather than restating
 * it: labels become printable at exactly the moment the requisition does, and
 * that helper already carries the Junction-only, submitted, and past-`ordered`
 * rules.
 */
export function hasLabelsAvailable(order: {
  internalStatus: LabInternalStatus;
  vendorStatus?: LabOrderStatus | null;
  vendor?: LabVendor;
  collectionMethod?: LabCollectionMethod | null;
}): boolean {
  if (order.collectionMethod !== "on_site_collection") return false;
  if (order.vendor === "in_house") {
    // The label carries the order number the analyzer scans, so it is there from
    // the moment the lab interface has the order until results start coming back.
    return (
      order.internalStatus === "submitted" && order.vendorStatus === "received"
    );
  }
  if (!hasRequisitionAvailable(order)) return false;
  // The requisition milestone opens the window but does not close it: a
  // requisition stays viewable for the life of the order, whereas a label is
  // only meaningful while the tube is still in the clinic. Once the sample is
  // with the lab, printing again would stamp today's date on a draw that
  // happened days ago — so the upper bound is deliberately *not* inherited.
  const prefix = vendorStatusPrefix(order.vendorStatus);
  return prefix === "received" || prefix === "collecting_sample";
}

/**
 * Whether Junction's collection-instructions PDF is offered for this order.
 * Offered for Quest and Labcorp on-site orders. Junction builds the PDF about
 * five minutes after the order is created and never updates the order's
 * collection-instructions flag when it's ready, so the rule is checked here
 * instead of the flag.
 */
export function hasCollectionInstructionsAvailable(order: {
  internalStatus: LabInternalStatus;
  vendor?: LabVendor;
  labSlug?: string;
  collectionMethod?: LabCollectionMethod | null;
}): boolean {
  if (order.vendor && order.vendor !== "junction") return false;
  return (
    order.internalStatus === "submitted" &&
    (order.labSlug === "quest" || order.labSlug === "labcorp") &&
    order.collectionMethod === "on_site_collection"
  );
}

/**
 * Whether the order can still be cancelled from Decoda. Junction accepts a
 * cancel while pre-lab; Fullscript has no cancellation endpoint at all.
 */
export function canCancelOrder(order: {
  vendorStatus?: LabOrderStatus | null;
  vendor?: LabVendor;
  internalStatus?: LabInternalStatus;
}): boolean {
  // Fullscript has no cancellation endpoint at all.
  if (order.vendor === "fullscript") return false;
  // Access exposes no cancellation endpoint either.
  if (order.vendor === "access") return false;
  if (order.vendor === "in_house") {
    // Until results start coming back: the analyzer may not have run it yet.
    if (order.internalStatus === "submitted")
      return order.vendorStatus === "received";
    // An order marked not sent may still have reached the lab, its answer lost.
    return (
      order.internalStatus === "failed" && order.vendorStatus !== "cancelled"
    );
  }
  // Junction:
  // A draft was never sent and a failed order never landed, so neither has
  // anything to cancel. Both carry a null vendor_status, which
  // vendorStatusPrefix reads as "received" — the same default that makes a
  // live pre-lab order cancellable. Mirrors hasRequisitionAvailable below.
  if (order.internalStatus && order.internalStatus !== "submitted")
    return false;
  const prefix = vendorStatusPrefix(order.vendorStatus);
  return prefix === "received" || prefix === "collecting_sample";
}

/**
 * Whether a results PDF exists to download from Decoda. The Junction rule matches
 * no Fullscript state, hence the vendor branch. Available from `partial_results`
 * onward — the PDF is the report as the lab wrote it.
 */
export function canDownloadResultPdf(order: {
  vendorStatus?: LabOrderStatus | null;
  vendor?: LabVendor;
  hasResultPdf?: boolean;
}): boolean {
  if (order.vendor === "fullscript") {
    return isResultsPhase(order.vendorStatus, "fullscript");
  }
  if (order.vendor === "access") {
    // The report PDF arrives inline with the results themselves, so it exists
    // from the first partial record onward — the report as Access wrote it.
    return isResultsPhase(order.vendorStatus, "access");
  }
  // Unlike every other helper here, uploaded must NOT fall through to
  // Junction's rule: there is no `/lab/order/{id}/result-pdf` for an uploaded
  // order — the adapter has nothing to fetch — so offering the button would
  // 404/500.
  if (order.vendor === "uploaded") return false;
  // The in-house lab may send its results without a report.
  if (order.vendor === "in_house") return !!order.hasResultPdf;
  return (
    vendorStatusPrefix(order.vendorStatus) === "completed" ||
    !!order.vendorStatus?.includes("partial_results")
  );
}
